import { Alert, Autocomplete, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, Snackbar, TextField } from "@mui/material";
import { useEffect, useMemo, useState } from "react";

const NONE_BIBLE = { _id: "", name: "None", isNone: true };

const getBibleId = (bible) => {
  const id = bible?._id ?? bible?.id ?? bible;

  if (id && typeof id === "object") {
    return id.$oid || id._id || id.id || "";
  }

  return id || "";
};

const getBibleName = (bible) => {
  if (bible?.isNone) {
    return "None";
  }

  return bible?.name || bible?.seriesName || bible?.title || bible?.bibleName || bible?.label || "Untitled Bible";
};

const ModalEditBible = ({ open, setOpen, projectId, projectBible, onSaved }) => {
  const [bibles, setBibles] = useState([]);
  const [selectedBible, setSelectedBible] = useState(NONE_BIBLE);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ open: false, message: "", severity: "success" });

  const options = useMemo(() => [NONE_BIBLE, ...bibles], [bibles]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const fetchBibles = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${process.env.REACT_APP_URL}/api/projects/bibles`, {
          headers: { "Content-Type": "application/json" },
        });
        const data = await response.json();

        if (!response.ok || data?.status === false) {
          throw new Error(data?.msg || data?.message || "Unable to load the available Bibles");
        }

        const availableBibles = Array.isArray(data)
          ? data
          : data?.bibles || data?.data?.bibles || data?.data || [];
        const normalizedBibles = Array.isArray(availableBibles) ? availableBibles : [];
        const currentBibleId = getBibleId(projectBible);
        const currentBible = normalizedBibles.find((bible) => getBibleId(bible) === currentBibleId);
        const selectedCurrentBible = currentBible || (currentBibleId
          ? { ...projectBible, _id: currentBibleId, name: getBibleName(projectBible) }
          : NONE_BIBLE);

        // Keep an already assigned Bible visible and selected even if it is not
        // returned by the available-Bibles endpoint.
        setBibles(currentBible || !currentBibleId
          ? normalizedBibles
          : [selectedCurrentBible, ...normalizedBibles]);
        setSelectedBible(selectedCurrentBible);
      } catch (error) {
        setBibles([]);
        setSelectedBible(NONE_BIBLE);
        setToast({ open: true, message: error.message || "Unable to load the available Bibles", severity: "error" });
      } finally {
        setLoading(false);
      }
    };

    fetchBibles();
  }, [open, projectBible]);

  const handleClose = () => {
    if (!saving) {
      setOpen(false);
    }
  };

  const handleConfirm = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_URL}/api/projects/${projectId}/bible`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          bibleId: selectedBible?.isNone ? null : getBibleId(selectedBible),
        }),
      });
      const data = await response.json();

      if (!response.ok || data?.status === false) {
        throw new Error(data?.msg || data?.message || "Unable to update the Bible");
      }

      onSaved?.(selectedBible?.isNone ? null : selectedBible);
      setToast({ open: true, message: "Bible updated successfully", severity: "success" });
      setOpen(false);
    } catch (error) {
      setToast({ open: true, message: error.message || "Unable to update the Bible", severity: "error" });
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        aria-labelledby="edit-bible-dialog-title"
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { backgroundColor: "#28262E", color: "white" } }}
      >
        <DialogTitle id="edit-bible-dialog-title">Edit Bible</DialogTitle>
        <DialogContent sx={{ pt: "20px !important" }}>
          {loading ? (
            <div style={{ display: "flex", justifyContent: "center", padding: "20px" }}>
              <CircularProgress aria-label="Loading available Bibles" />
            </div>
          ) : (
            <Autocomplete
              value={selectedBible}
              onChange={(_, value) => setSelectedBible(value || NONE_BIBLE)}
              options={options}
              getOptionLabel={getBibleName}
              isOptionEqualToValue={(option, value) => getBibleId(option) === getBibleId(value)}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Bible"
                  placeholder="Search by name"
                  inputProps={{ ...params.inputProps, "aria-label": "Search and select a Bible" }}
                />
              )}
              sx={{
                "& .MuiInputLabel-root, & .MuiInputBase-input, & .MuiSvgIcon-root": { color: "white" },
                "& .MuiOutlinedInput-notchedOutline": { borderColor: "white" },
                "&:hover .MuiOutlinedInput-notchedOutline, & .Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "white" },
              }}
              ListboxProps={{ "aria-label": "Available Bibles" }}
              slotProps={{
                paper: {
                  sx: {
                    backgroundColor: "#28262E",
                    color: "white",
                    "& .MuiAutocomplete-option[aria-selected='true']": { backgroundColor: "#4a4a4a" },
                    "& .MuiAutocomplete-option.Mui-focused": { backgroundColor: "#3f3f3f" },
                  },
                },
              }}
            />
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleClose} disabled={saving} sx={{ color: "white" }}>Cancel</Button>
          <Button variant="contained" onClick={handleConfirm} disabled={loading || saving}>
            {saving ? <CircularProgress size={20} color="inherit" aria-label="Saving Bible" /> : "Confirm"}
          </Button>
        </DialogActions>
      </Dialog>
      <Snackbar
        open={toast.open}
        autoHideDuration={3000}
        onClose={() => setToast((currentToast) => ({ ...currentToast, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert severity={toast.severity}>{toast.message}</Alert>
      </Snackbar>
    </>
  );
};

export default ModalEditBible;
